import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { transitionHeight } from 'animations-lib';
import { Namespace } from 'enums-lib';
import { IConfigFilter, IDocumentDestinationConfig, IIconService } from 'interfaces-lib';
import { BehaviorSubject, Subscription } from 'rxjs';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { IdentityStorage } from 'storage-service-lib';
import { IDocumentDestinationService } from '../../../model/interfaces/idocumentdestinationservice';
import { IEntityLabelPair } from '../../../services/entity-type/entity-type.service';
import { ServiceProviderServiceFactory } from '../../../services/service-providers/service-provider-service.factory';

@Component({
  selector: 'app-document-destination',
  templateUrl: './document-destination.component.html',
  styleUrls: ['./document-destination.component.scss'],
  animations: [transitionHeight],
  standalone: false,
})
export class DocumentDestinationComponent implements OnInit, OnDestroy {
  @Input() configuration: UntypedFormGroup;
  @Input() isNewConfig: boolean;

  private subscriptions: Subscription = new Subscription();
  private entityMeta: IEntityLabelPair[] = undefined;
  private readonly namespace: Namespace;
  private documentDestinationService: IDocumentDestinationService;
  documentDestinationOptions: IDocumentDestinationConfig[] | undefined = undefined;
  loading$: BehaviorSubject<boolean>;
  iconService: IIconService;

  constructor(private identityStorage: IdentityStorage, private serviceProviderServiceFactory: ServiceProviderServiceFactory,
              private sharedServiceProviderServiceFactory: SharedServiceProviderServiceFactory) {
    this.namespace = this.identityStorage.get().namespace;
    this.documentDestinationService = this.serviceProviderServiceFactory
      .getServiceProviderService(this.namespace)
      .getDocumentDestinationService();
    this.iconService = this.sharedServiceProviderServiceFactory.getServiceProviderService(this.namespace).getIconService();
    this.loading$ = this.documentDestinationService.loading$;
  }

  ngOnInit(): void {
    this.createSubscriptions();
  }

  get documentDestinationFormArray() {
    return this.configuration?.get('documentDestination') as UntypedFormArray;
  }

  get selectedAvailability(): string[] | undefined {
    return (this.configuration?.get('filters')?.value as IConfigFilter[])?.find((x) => x.filterType === 'AVAILABILITY')
      ?.filterValues as string[];
  }

  get selectedEntity(): string | undefined {
    return this.configuration?.get('entity')?.value;
  }

  getOptionPaths(index: number): string[] {
    return this.documentDestinationFormArray?.controls[index]?.get('paths')?.value ?? [];
  }

  pathDisplay(path: string) {
    return this.documentDestinationService.getMetaLabelFromDestinationPath(this.entityMeta, path);
  }

  private createSubscriptions() {
    this.subscriptions.add(
      this.configuration.get('entity').valueChanges.subscribe((_) => {
        this.updateDocumentDestinationOptions();
      })
    );

    this.subscriptions.add(
      this.configuration.get('filters').valueChanges.subscribe((_) => {
        this.updateDocumentDestinationOptions(true);
      })
    );

    this.documentDestinationService.documentDestinationMeta$.subscribe((meta) => {
      this.entityMeta = meta;
      this.updateDocumentDestinationOptions();
    });
  }

  private updateDocumentDestinationOptions(force?: boolean) {
    if (!this.selectedEntity || !this.entityMeta) {
      return;
    }

    this.documentDestinationService
      .getDocumentDestinationOptions(this.entityMeta, this.selectedEntity, this.selectedAvailability)
      .subscribe((options) => {
        this.documentDestinationOptions = options;

        const rebuildFormArray = this.isNewConfig || !this.documentDestinationFormArray?.controls.length || force;

        if (rebuildFormArray) {
          this.rebuildFormArray();
        }

        // this is for edge case legacy templates - should not get hit very often as only affects a few templates
        if (this.documentDestinationOptions.length !== this.documentDestinationFormArray.controls.length) {
          this.fixFormArray();
        }
      });
  }

  private rebuildFormArray() {
    this.clearFormArray(this.documentDestinationFormArray);
    for (let i = 0; i < this.documentDestinationOptions.length; i++) {
      this.documentDestinationFormArray.push(
        new UntypedFormGroup({
          entityType: new UntypedFormControl(this.documentDestinationOptions[i].entityType, Validators.required),
          paths: new UntypedFormControl(
            this.documentDestinationOptions[i].options.filter((x) => x.default).map((y) => y.path),
            Validators.required
          ),
        })
      );
    }
  }

  private clearFormArray = (formArray: UntypedFormArray) => {
    while (formArray.length !== 0) {
      formArray.removeAt(0);
    }
  }

  private fixFormArray(): void {
    if (!this.documentDestinationOptions || !this.documentDestinationFormArray) {
      return;
    }

    // Filter out unique entity types and add them to form array if missing
    this.documentDestinationOptions
      .filter(option => option && option.entityType) // Ensure each option and its entityType exists
      .map(option => option.entityType)
      .forEach(entityType => {
        const exists = this.documentDestinationFormArray.controls.some(control => {
          const entityTypeControl = control?.get('entityType');
          return entityTypeControl && entityTypeControl.value === entityType;
        });

        if (!exists) {
          this.documentDestinationFormArray.push(
            new UntypedFormGroup({
              entityType: new UntypedFormControl(entityType, Validators.required),
              paths: new UntypedFormControl([], Validators.required),
            })
          );
        }
      });
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
